// Port of Flutter's SpinningFanIcon / FanBladesPainter (fanWidgets.dart).
//
// Three swept blades at 120° apart plus a hub, drawn as SVG paths with exactly
// the Dart's bezier control points (expressed in units of the radius r).
//
// The angle ACCUMULATES from frame delta time rather than being driven by a CSS
// animation with a duration. That is deliberate and matches the Dart: changing
// speed then changes only the RATE, so the blades keep their current position
// instead of snapping back to zero — a speed change reads as smooth.
//
// speedFraction: 0 = stopped, 1 = full. Rate ≈ 0.9 → 5.6 rad/s across the range.

import React, { useEffect, useRef } from 'react';

function bladePath(r) {
  return [
    `M ${r * 0.12} ${-r * 0.06}`,
    `Q ${r * 0.55} ${-r * 0.44} ${r * 0.96} ${-r * 0.17}`,
    `Q ${r * 1.0} ${r * 0.03} ${r * 0.7} ${r * 0.15}`,
    `Q ${r * 0.36} ${r * 0.23} ${r * 0.12} ${r * 0.06}`,
    'Z',
  ].join(' ');
}

export default function SpinningFan({
  size, speedFraction, bladeColor, idleColor, hubColor,
}) {
  const groupRef = useRef(null);
  const angleRef = useRef(0);
  const lastRef = useRef(0);
  const fracRef = useRef(speedFraction);
  fracRef.current = speedFraction;

  useEffect(() => {
    let raf;
    const tick = (t) => {
      const dt = lastRef.current ? (t - lastRef.current) / 1000 : 0;
      lastRef.current = t;
      const f = fracRef.current;
      const rate = f > 0 ? 0.9 + f * 4.7 : 0;
      if (rate > 0) {
        angleRef.current = (angleRef.current + dt * rate) % (2 * Math.PI);
        if (groupRef.current) {
          groupRef.current.setAttribute(
            'transform',
            `rotate(${(angleRef.current * 180) / Math.PI})`,
          );
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const r = size / 2;
  const running = speedFraction > 0;
  const colour = running ? bladeColor : idleColor;
  const d = bladePath(r);

  return (
    <svg width={size} height={size} viewBox={`${-r} ${-r} ${size} ${size}`} style={{ overflow: 'visible' }}>
      <defs>
        <filter id="sli-fan-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation={r * 0.06} />
        </filter>
      </defs>
      <g ref={groupRef}>
        {running && [0, 120, 240].map((deg) => (
          <path key={`g${deg}`} d={d} transform={`rotate(${deg})`}
            fill={colour} opacity={0.45} filter="url(#sli-fan-glow)" />
        ))}
        {[0, 120, 240].map((deg) => (
          <path key={deg} d={d} transform={`rotate(${deg})`} fill={colour} />
        ))}
      </g>
      {/* Hub stays unrotated so it looks still. */}
      <circle cx={0} cy={0} r={r * 0.16} fill="rgba(255,255,255,0.9)" />
      <circle cx={0} cy={0} r={r * 0.10} fill={hubColor} />
    </svg>
  );
}
